import { HelpCircle, Send } from "lucide-react";
import { useState } from "react";

import { Button } from "@bigpp/ui/components/button";

// Prominent inline reply card the agent's `ask_user` tool renders while the
// run is paused waiting for input. Kept visually separate from the collapsed
// tool-call list so the user sees it immediately.
type Props = {
  question: string;
  options?: readonly string[];
  answer?: string;
  status: "running" | "ok" | "error";
  onAnswer: (answer: string) => void;
};

export function AskUserCard({ question, options, answer, status, onAnswer }: Props) {
  const [text, setText] = useState("");
  const done = status !== "running";

  return (
    <div className="rounded-md border border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5 p-2.5">
      <div className="flex items-start gap-2">
        <HelpCircle className="mt-0.5 size-3.5 shrink-0 text-[var(--color-primary)]" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="text-xs text-[var(--color-foreground)]">{question}</p>

          {done ? (
            <div className="rounded bg-[var(--color-card)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
              {answer ?? "(answered)"}
            </div>
          ) : (
            <>
              {options && options.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        onAnswer(opt);
                      }}
                      className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2.5 py-1 text-[11px] hover:bg-[var(--color-secondary)] hover:-translate-y-px hover:shadow-sm transition-all"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const trimmed = text.trim();
                  if (!trimmed) return;
                  onAnswer(trimmed);
                }}
                className="flex items-center gap-1"
              >
                <input
                  type="text"
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                  }}
                  placeholder={options && options.length > 0 ? "Or type your own…" : "Type your answer…"}
                  className="flex-1 rounded border border-[var(--color-input)] bg-[var(--color-card)] px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="icon-sm"
                  disabled={text.trim().length === 0}
                  title="Send answer"
                >
                  <Send />
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
